import {
	Avatar,
	Button,
	FormLabel,
	Link,
	Switch,
	Typography,
	useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataMultiSelect from "../components/DataInputs/DataMultiSelect";
import DataSelect from "../components/DataInputs/DataSelect";
import DataSwitch from "../components/DataInputs/DataSwitch";
import DataTextField from "../components/DataInputs/DataTextField";
import EditMenu from "../components/EditMenu";
import Loader from "../components/Loader";
import { InfinityPagination } from "../hooks/Pagination";
import { ManagerSearch, ManagerTable } from "../interfaces/Manager";
import { City } from "../interfaces/Region";
import { TestTable } from "../interfaces/Test";
import {
	ProbationCreate,
	SelectedVacancyTests,
	VacancyTest,
} from "../interfaces/Vacancy";
import {
	CreateProbation,
	DeleteProbation,
	GetDirections,
	GetManagers,
	GetProbation,
	GetRegions,
	GetSpecilizations,
	GetTests,
	UpdateProbation,
} from "../requester/functions";
import { ALT_URL, MAIN_URL } from "../requester/requester";
import VO, { VK, getUrlId } from "../utilities/validate";
import Specilization, { Direction } from "./Specilization";

interface IProps {}
const template: ProbationCreate = {
	name: "",
	description: "",
	program_for: "",
	what_tasks_need_to_be_solved: "",
	id_direction: 0,
	id_city: 0,
	id_specialization: 0,
	learn_to: "",
	result_of_your_internship: "",
	remote: false,
	is_negotiable: false,
};

const ProbationPage = (props: IProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();

	const { enqueueSnackbar } = useSnackbar();

	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);

	const [vacancy, setVacancy] = useState<ProbationCreate>(template);
	const [directions, setDirections] = useState<Direction[]>([]);
	const [specilizations, setSpecilizations] = useState<Specilization[]>([]);
	const [cities, setCities] = useState<City[]>([]);

	const [managers, setManagers] = useState<ManagerTable[]>([]);
	const [searchManager, setSearchManager] = useState<ManagerSearch>({});

	const [tests, setTests] = useState<TestTable[]>([]);
	const [selectedTests, setSelectedTests] = useState<SelectedVacancyTests>({});

	const buildTests = () => {
		var result: VacancyTest[] = [];
		if (selectedTests.notRequired)
			result = selectedTests.notRequired.map((t) => {
				const item: VacancyTest = {
					id: t,
					required: false,
				};
				return item;
			});
		if (selectedTests.required)
			result = [
				...result,
				...selectedTests.required.map((t) => {
					const item: VacancyTest = {
						id: t,
						required: true,
					};
					return item;
				}),
			];

		return result as VacancyTest[];
	};

	const setManager = (id: number, request_id: number) => {
		setVacancy((v) => {
			v.id_manager = id;
			return { ...v };
		});
	};
	const renderManager = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`manager-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setManager(item.id, rowId);
			}}
		>
			<Avatar src={`${MAIN_URL}manager/avatar/${item.avatar}`} />
			<Typography>
				{item.last_name} {item.first_name}
			</Typography>
		</div>
	);

	useEffect(() => {
		const idVacancy: string = getUrlId(location.pathname);

		GetDirections({}, (resp) => {
			if (resp.status === "ok" && resp.data) setDirections(resp.data);
		});

		GetSpecilizations({}, (resp) => {
			if (resp.status === "ok" && resp.data) setSpecilizations(resp.data);
		});

		GetRegions({}, (resp) => {
			if (resp.status === "ok" && resp.data) {
				var eventRegions: City[] = [];
				resp.data.forEach((r) => {
					eventRegions.push(...r.cities);
				});
				setCities(eventRegions);
			}
		});

		if (idVacancy === "create") {
			setIsLoaded(true);
		} else {
			GetProbation({ id: parseInt(idVacancy) }, (resp) => {
				if (resp.status === "ok" && resp.data) {
					setVacancy(resp.data);

					setSelectedTests((t) => {
						t.required = [];
						t.notRequired = [];

						if (resp.data?.tests)
							resp.data?.tests.forEach((test) => {
								if (test.required) t.required?.push(test.id);
								else t.notRequired?.push(test.id);
							});
						return { ...t };
					});
					setIsLoaded(true);
					setIsEdit(true);
				} else {
					navigate("/vacancies");
				}
			});
		}

		GetTests(
			{
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok") {
					if (resp.data) {
						setTests(resp.data.data);
					}
				}
			}
		);
	}, []);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchManager,
				pagination: { page: 0, size: 50 },
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setManagers(resp.data.data);
				}
			}
		);
	}, [searchManager]);

	return isLoaded ? (
		<div className="flex flex-col gap-5">
			{vacancy.id && vacancy.id > 0 ? (
				<div>
					<Link
						href={`${ALT_URL}stazhirovka/${vacancy.id}`}
						target="_blank"
						rel="noopener norefrrer"
					>
						Эта стажировка на jobmf.tj
					</Link>
				</div>
			) : null}
			<div className="w-full flex gap-12">
				<div className="flex flex-col gap-5 w-[400px]">
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"name"}
						inputProps={{
							required: true,
							label: "Имя",
							error: !VK(vacancy.name),
						}}
					/>
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"description"}
						multiline
						inputProps={{
							required: true,
							label: "Описание",
							minRows: 3,
							variant: "outlined",
							error: !VK(vacancy.description),
						}}
					/>
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"program_for"}
						multiline
						inputProps={{
							required: true,
							label: "Для кого эта программа",
							minRows: 3,
							variant: "outlined",
							error: !VK(vacancy.program_for),
						}}
					/>
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"what_tasks_need_to_be_solved"}
						multiline
						inputProps={{
							required: true,
							label: "Какие задачи необходимо решать",
							minRows: 3,
							variant: "outlined",
							error: !VK(vacancy.what_tasks_need_to_be_solved),
						}}
					/>
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"learn_to"}
						multiline
						inputProps={{
							required: true,
							label: "В процессе прохождения программы вы научитесь",
							minRows: 3,
							variant: "outlined",
							error: !VK(vacancy.learn_to),
						}}
					/>
					<DataTextField
						value={vacancy}
						className="w-full"
						setValue={setVacancy}
						keyValue={"result_of_your_internship"}
						multiline
						inputProps={{
							required: true,
							label: "Кем вы сможете стать по итогам стажировки",
							minRows: 3,
							variant: "outlined",
							error: !VK(vacancy.result_of_your_internship),
						}}
					/>
				</div>
				<div className="flex flex-col gap-5 w-[400px]">
					<DataSelect
						list={directions}
						value={vacancy}
						setValue={setVacancy}
						keyValue={"id_direction"}
						label={"Направление"}
						inputProps={{
							required: true,
							error: !VK(vacancy.id_direction),
						}}
						className="w-full"
					/>
					<DataSelect
						list={specilizations}
						value={vacancy}
						setValue={setVacancy}
						keyValue={"id_specialization"}
						label={"Специальность"}
						inputProps={{
							required: true,
							error: !VK(vacancy.id_specialization),
						}}
						className="w-full"
					/>
					<DataSelect
						list={cities}
						value={vacancy}
						setValue={setVacancy}
						keyValue={"id_city"}
						label={"Город"}
						inputProps={{
							required: true,
							error: !VK(vacancy.id_city),
						}}
						className="w-full"
					/>
					<DataSwitch
						value={vacancy}
						setValue={setVacancy}
						keyValue={"remote"}
						label="Дистанционная"
					/>
					<DataSwitch
						value={vacancy}
						setValue={setVacancy}
						keyValue={"is_negotiable"}
						label="Договорная цена"
					/>
					<Typography variant="caption" className="!mt-3">
						Отвественный
					</Typography>
					<EditMenu
						renderElement={renderManager}
						data={managers}
						search={searchManager.full_name}
						selectedId={vacancy.id_manager}
						item={vacancy}
						setSearch={function (value: string) {
							setSearchManager((s) => {
								s.full_name = value;
								return { ...s };
							});
						}}
					>
						{managers.find((m) => m.id === vacancy.id_manager || 0)
							?.first_name || "Нет"}
					</EditMenu>
					<Button
						variant="contained"
						disabled={!VO(vacancy, template)}
						onClick={() => {
							if (!isEdit)
								CreateProbation(
									{ ...vacancy, tests: buildTests() as VacancyTest[] },
									(resp) => {
										if (resp.status === "ok" && resp.data) {
											setIsEdit(true);
											navigate(`/probation/${resp.data.id}`);
											enqueueSnackbar("Стажировка успешно создана", {
												variant: "success",
											});
										} else {
											enqueueSnackbar(`Ошибка ${resp.code}, ${resp.message}`, {
												variant: "error",
											});
										}
									}
								);
							else
								UpdateProbation(
									{ ...vacancy, tests: buildTests() as VacancyTest[] },
									(resp) => {
										if (resp.status === "ok") {
											enqueueSnackbar("Стажировка успешно обновлена", {
												variant: "success",
											});
										} else {
											enqueueSnackbar(`Ошибка ${resp.code}, ${resp.message}`, {
												variant: "error",
											});
										}
									}
								);
						}}
					>
						Cохранить
					</Button>
					<div className="flex flex-col gap-5 w-[400px]">
						<DataMultiSelect
							label="Не обязательные тесты"
							list={tests}
							value={selectedTests}
							setValue={(v: SelectedVacancyTests) => {
								setSelectedTests({ ...v });
							}}
							keyItemValue={"id"}
							keyItemTitle={"name"}
							keyValue={"notRequired"}
						/>
						<DataMultiSelect
							label="Обязательные тесты"
							list={tests}
							value={selectedTests}
							setValue={(v: SelectedVacancyTests) => {
								setSelectedTests({ ...v });
							}}
							keyItemValue={"id"}
							keyItemTitle={"name"}
							keyValue={"required"}
						/>
					</div>
					{isEdit ? (
						<div>
							<Switch
								color="error"
								checked={vacancy.is_delete}
								onChange={(ev) => {
									var eventVacancy = vacancy;
									eventVacancy.is_delete = ev.target.checked;
									setVacancy({ ...eventVacancy });
									DeleteProbation(
										{ id: vacancy.id || 0, is_delete: ev.target.checked },
										(resp) => {
											if (resp.status === "ok") {
												if (ev.target.checked)
													enqueueSnackbar("Стажировка архивирована", {
														variant: "warning",
													});
												else
													enqueueSnackbar("Стажировка разархивирована", {
														variant: "warning",
													});
											}
										}
									);
								}}
							/>
							<FormLabel>Архивированный</FormLabel>
						</div>
					) : null}
				</div>
			</div>
		</div>
	) : (
		<Loader />
	);
};

export default ProbationPage;
